/**
 * Sentry Configuration
 * Extracted from main.tsx for cleaner architecture
 *
 * Initializes Sentry for error tracking and performance monitoring.
 * Only runs in production when VITE_SENTRY_DSN is provided.
 */

/**
 * Initialize Sentry asynchronously to avoid blocking render
 * Called from main.tsx after initial app setup
 */
export async function initSentry(): Promise<void> {
  const isProduction = import.meta.env.MODE === 'production';
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!isProduction) {
    if (sentryDsn) {
      console.log('ℹ️ Sentry: Disabled in development environment');
    }
    return;
  }

  if (!sentryDsn) {
    console.warn('⚠️ Sentry: DSN not provided. Error tracking disabled.');
    return;
  }

  try {
    const Sentry = await import('@sentry/react');

    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.VITE_ENVIRONMENT || 'production',
      integrations: [
        Sentry.browserTracingIntegration({
          enableLongTask: true,
          enableInp: true,
        }),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
          maskAllInputs: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: parseFloat(import.meta.env.VITE_PERFORMANCE_SAMPLE_RATE || '0.1'),
      // Session Replay - 10% normal sessions, 100% error sessions
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      release: import.meta.env.VITE_SENTRY_RELEASE || 'unknown',
      initialScope: {
        tags: {
          component: 'frontend',
          deployment: 'vercel',
        },
      },
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        '__gCrWeb',
        'originalCreateNotification',
        'atomicFindClose',
        // Network errors
        'Non-Error promise rejection captured',
        'ChunkLoadError',
        'Loading chunk',
        'Loading CSS chunk',
        // ResizeObserver errors
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // CSP violations
        'Content Security Policy',
        'Blocked a frame with origin',
        // Common network errors
        'NetworkError',
        'Failed to fetch',
        'ERR_NETWORK',
        'ERR_INTERNET_DISCONNECTED',
        // CORS errors
        'Script error',
      ],
      beforeSend(event, hint) {
        // Filter out errors from browser extensions
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;

        if (frames?.some(frame =>
          frame.filename?.includes('chrome-extension://') ||
          frame.filename?.includes('moz-extension://') ||
          frame.filename?.includes('safari-extension://') ||
          frame.filename?.includes('extension') ||
          !frame.filename ||
          frame.filename === '<anonymous>'
        )) {
          return null;
        }

        // Filter out errors from third-party scripts
        const error = hint.originalException;
        if (typeof error === 'object' && error && 'stack' in error) {
          const stack = String(error.stack);
          if (stack.includes('google') || stack.includes('facebook') || stack.includes('twitter')) {
            return null;
          }
        }

        return event;
      },
    });

    console.log('✅ Sentry: Initialized for production monitoring');
  } catch (error) {
    console.error('🚨 Sentry: Failed to initialize:', error);
  }
}
